<template>
    <div 
        class="invoice__wrap" 
        :class="{ 'relative disabled': isPageDisabled || checkUpgradeLoading && checkUpgradeFields.invoices }"
    > 
        <div>
            <template v-if="pageLoading">
                <PageSpinner />
            </template>

            <template v-else-if="pageLoadErr">
                <EmptyPage state="error" @action="handleFetchInvoice" />
            </template>

            <template v-else-if="unableToLoadPage">
                <EmptyPage state="error" @action="backToInvoicesPage" />
            </template>

            <template v-else>
                <div>
                    <div class="invoice__details__header border--0 mb--15">  
                        <div class="pb--0">
                            <div class="row__item pb--0 flex align-items-center">
                                <FormLabel :title="`Inv-${invoice.invoiceNo || 'N/A'}`" class="mb--0 text--semi--bold text--upper" label-type="tertiary" />
                                <Status type="invoice" :value="invoice.status === 'pending' ? 'issued' : invoice.status" class="ml--10" />
                            </div>
                            <p class="project__item__title text--xs invoice__detail">
                                Created on: {{ $format_date(invoice.createdAt) }}
                            </p>
                        </div>

                        <div class="relative flex align-items-center">
                            <div v-if="['failed', 'draft'].includes(invoice.status)" class="mr--5 relative pb--0">
                                <BaseButton 
                                    :label="invoice.status === 'draft' ? 'Send' : 'Resend'" 
                                    prefix-icon
                                    icon-name="send"
                                    variant="default" 
                                    variant-style="primary"
                                    @submit="handleSendInvoice"
                                />
                            </div>
                            
                            <div v-if="!['failed'].includes(invoice.status)" class="mr--5 relative pb--0">
                                <BaseButton 
                                    label="Download" 
                                    prefix-icon
                                    icon-name="download"
                                    icon-
                                    variant="outline" 
                                    variant-style="secondary"
                                    :disabled="downloadLoading"
                                    @submit="downloadInvoice"
                                />
                            </div>

                            <div class="dropdown mr--5">
                                <BaseButton 
                                    id="invoiceDetailsDropDown" 
                                    prefix-icon
                                    data-bs-toggle="dropdown" 
                                    aria-expanded="false"
                                    class="mr--5"
                                    icon-name="dots-horizontal-rounded"
                                    icon- 
                                    variant="outline" 
                                    variant-style="dark"
                                />

                                <List id="invoiceDetails">
                                    <ListItem 
                                        v-if="invoice.status === 'draft'"
                                        flex
                                        pointer
                                        @click="goToEditPage"
                                    >
                                        <IconSvg name="edit" />
                                        <span class="ml--10 text--sm">Edit draft</span>
                                    </ListItem>

                                    <ListItem 
                                        v-if="!['draft', 'fully paid'].includes(invoice.status)"
                                        flex
                                        pointer
                                        @click="modalAction = 'showInvoiceProgressModal'"
                                    >
                                        <IconSvg name="transfer" />
                                        <span class="ml--10 text--sm text-gray-800">Track status</span>
                                    </ListItem>
                                    
                                    <template v-if="!['draft', 'failed'].includes(invoice.status)">
                                        <ListItem 
                                            v-if="invoice.meta.billingMethod === 'personal_account'"
                                            flex
                                            pointer
                                            @click="openStatusModal"
                                        >
                                            <IconSvg name="spreadsheet" />
                                            <span class="ml--10 text--sm text-gray-800">Manually update status</span>
                                        </ListItem>
                                        <ListItem 
                                            flex
                                            pointer
                                            @click="handleDuplicateInvoice"
                                        >
                                            <IconSvg name="duplicate" width="16px" />
                                            <span class="ml--10 text--sm text-gray-800">Duplicate</span>
                                        </ListItem>
                                    </template>
                                   
                                    <ListItem 
                                        flex
                                        pointer
                                        @click="showDeleteModal = true"
                                    >
                                        <IconSvg name="delete" fill="#e04060" width="16px" />
                                        <span class="ml--10 text--sm text-error-400">Delete</span>
                                    </ListItem>
                                </List>
                            </div>
                        </div>
                    </div>

                    <div class="invoice__details__row">
                        <div class="invoice__details__row__left">
                            <div class="invoice__details__item pb--0">
                                <div class="invoice__details__group">
                                    <div>
                                        <div class="flex align-items-center mr--10">
                                            <FormLabel title="Currency" class="mb--0" label-type="menu" />
                                        </div>
                                        <p class="text--upper invoice__detail text--sm mt--5">
                                            {{ invoice.currency }}
                                        </p>
                                    </div>
                                </div>

                                <div v-if="invoice.status !== 'draft'" class="invoice__details__group">
                                    <div>
                                        <div class="flex align-items-center mr--10">
                                            <FormLabel title="Date Issued" class="mb--0" label-type="menu" />
                                        </div>
                                        <p class="invoice__detail text--sm mt--5">
                                            {{ $format_date(invoice.dateIssued) || "N/A" }}
                                        </p>
                                    </div>
                                </div>

                                <div class="invoice__details__group">
                                    <div>
                                        <div class="flex align-items-center mr--10">
                                            <FormLabel title="Due Date" class="mb--0" label-type="menu" />
                                        </div>
                                        <p class="invoice__detail text--sm mt--5">
                                            {{ $format_date(invoice.dueDate) }}
                                        </p>
                                    </div>
                                </div>

                                <div v-if="!['draft', 'fully paid'].includes(invoice.status)" class="invoice__details__group">
                                    <div>
                                        <div class="flex align-items-center mr--10">
                                            <FormLabel title="Amount Due" class="mb--0" label-type="menu" />
                                        </div>
                                        <p class="text--upper invoice__detail text--sm mt--5 text--capitalize">
                                            {{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(amountDue) }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="invoice__details__item align-items-baseline pb--0">
                                <div class="invoice__details__group">
                                    <div>
                                        <div class="flex align-items-center mr--10 mb--5">
                                            <IconSvg name="envelope" width="1rem" fill="#64748B" />
                                            <FormLabel title="Corresponding Emails" class="mb--0 ml--5" label-type="menu" />
                                        </div>

                                        <template v-if="!splitCCEmails(invoice.meta.ccEmails).length">
                                            <span class="text--sm text-gray-400">
                                                -
                                            </span>
                                        </template>

                                        <template v-else>
                                            <p v-for="email in splitCCEmails(invoice.meta.ccEmails)" :key="email" class="text--sm text-gray-400 mb--5 mt--5">
                                                {{ email || '-' }}
                                            </p>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <div class="invoice__details__item pb--0">
                                <div class="invoice__details__group w-100">
                                    <div>
                                        <div class="flex align-items-center mr--10">
                                            <IconSvg name="notes" width="1rem" fill="#64748B" />
                                            <FormLabel title="Memo" class="mb--0 ml--5" label-type="menu" />
                                        </div>
                                        <p class="invoice__details--text text--sm mt--10">
                                            {{ invoice.memo || '-' }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="mt--40">
                                <div class="col-12 mb--0">
                                    <FormLabel title="Line items" class="mb--0" label-type="title" />
                                </div>

                                <div class="col-12">
                                    <div class="invoice__item mt--5">
                                        <table class="table table-light mt--20">
                                            <thead>
                                                <tr>
                                                    <th scope="col" class="pl--0">
                                                        #
                                                    </th>
                                                    <th scope="col" class="pl--0">
                                                        Description
                                                    </th>
                                                    <th scope="col" class="pl--0">
                                                        Quantity
                                                    </th>
                                                    <th scope="col" class="pl--0">
                                                        Price
                                                    </th>
                                                    <th scope="col" class="pl--0">
                                                        Unit
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody v-for="(item, index) in invoice.meta.items" :key="item._id">
                                                <tr>
                                                    <td scope="row">
                                                        {{ index + 1 }}
                                                    </td>
                                                    <td>{{ item.itemName || '-' }}</td>
                                                    <td>{{ item.itemQuantity || 0 }}</td>
                                                    <td>{{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(item.itemPrice) || 0 }}</td>
                                                    <td>{{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(item.itemUnit) || 0 }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div class="col-12 mt--30">
                                <div class="mb--5">
                                    <FormLabel title="Others" class="mb--0" label-type="title" />
                                </div>

                                <div class="pb--15 grid gap-2">
                                    <div class="justify-content-between flex align-items-center">
                                        <div class="invoice__compile--label">
                                            Subtotal:
                                        </div>
                                        <div class="invoice__compile--value text--medium text--upper">
                                            {{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(computedSubTotal, 2) }}
                                        </div>
                                    </div>

                                    <div class="justify-content-between flex align-items-center">
                                        <div class="invoice__compile--label">
                                            {{ discountBtnLabel }}
                                        </div>
                                        <div class="invoice__compile--value text--medium text--upper">
                                            -{{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(computedDiscount, 2) }}
                                        </div>
                                    </div>

                                    <div class="justify-content-between flex align-items-center">
                                        <div class="invoice__compile--label">
                                            <!-- Tax <span v-if="taxPercentage.show">({{ taxPercentage.value }}%)</span>: -->
                                            Tax:
                                        </div>
                                        <div class="justify-content-between flex align-items-center text--sm text--medium text--upper">
                                            {{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(computedTaxes, 2) }}
                                        </div>
                                    </div>
                                    
                                    <div class="flex align-items-center justify-content-between">
                                        <div class="invoice__details--text">
                                            Total:
                                        </div>
                                        <div class="invoice__details--text">
                                            <span class="text--semi--bold text--upper">
                                                {{ $country.getSymbol(invoice.currency, 'currency') }}{{ $format_number(invoice.totalAmount, 2) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="invoice__details__divider" />

                            <div class="mt--30">
                                <FormLabel title="Payment Information" class="mb--15" label-type="title" />                        
                                <div class="col-12 flex align-items-baseline">
                                    <div class="flex-1">
                                        <div class="invoice__details__group w-100">
                                            <div class="w-100">
                                                <div class="invoice__client--bio stack w-100">
                                                    <p>
                                                        <FormLabel title="Payment Method" class="mb--0" label-type="menu" />
                                                    </p>
                                                    <p v-if="getPaymentMethodName(invoice.meta.billingMethod)">
                                                        {{ getPaymentMethodName(invoice.meta.billingMethod) || '-' }}
                                                    </p>
                                                </div>
                                               
                                                <template v-if="invoice.meta.billingMethod === 'payment_link'">
                                                    <div class="flex align-items-center mt--3">
                                                        <a class="project__item__title flex align--items--center mt--0 text--sm text--underline" :href="linkData.url" target="_blank">
                                                            <span class="project__item__title mt--0 text--sm mr--5">
                                                                {{ linkData.name }} payment link
                                                            </span>
                                                            <IconSvg name="link" />
                                                        </a>
                                                    </div>
                                                </template>

                                                <template v-else-if="invoice.meta.billingMethod === 'personal_account'">
                                                    <div class="w-100">
                                                        <div>
                                                            <p class="text--sm text--semi--bold text-gray-600 mb--5">
                                                                Account Details
                                                            </p>
                                                            <div class="invoice__details__text">
                                                                <div class="invoice__client--bio stack">
                                                                    <p>
                                                                        Account name:
                                                                    </p>
                                                                    <p>
                                                                        {{ accountDetails.accountName || '-' }}
                                                                    </p>
                                                                </div>
                                                                <div class="invoice__client--bio stack">
                                                                    <p>
                                                                        Bank name:
                                                                    </p>
                                                                    <p>
                                                                        {{ accountDetails.bankName || '-' }}
                                                                    </p>
                                                                </div>
                                                                <div class="invoice__client--bio stack">
                                                                    <p>
                                                                        Account number:
                                                                    </p>
                                                                    <p>
                                                                        {{ accountDetails.accountNumber || '-' }}
                                                                    </p>
                                                                </div>
                                                                <div class="invoice__client--bio stack">
                                                                    <p>
                                                                        IBAN:
                                                                    </p>
                                                                    <p>
                                                                        {{ accountDetails.routingNumber || '-' }}
                                                                    </p>
                                                                </div>
                                                                <div class="invoice__client--bio stack">
                                                                    <p>
                                                                        Sort code:
                                                                    </p>
                                                                    <p>
                                                                        {{ accountDetails.swift || '-' }}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                                <template v-else />
                                            </div> 
                                        </div>
                                    </div>

                                    <div class="flex-1">
                                        <div class="invoice__details__group w-100">
                                            <div>
                                                <FormLabel title="Tax Details" class="mb--0" label-type="menu" />
                                                
                                                <template v-if="invoice.meta.tax && invoice.meta.tax.length">
                                                    <ul>
                                                        <li v-for="item in invoice.meta.tax" :key="item._id" class="flex align-items-center mb--10">
                                                            <div class="flex align-items-center">
                                                                <span class="box-icon block" :class="[item.taxType]" />
                                                                <div class="flex align-items-center">
                                                                    <span class="sub-title mr--5">{{ mapTaxTypes[item.taxName] }} | </span> 
                                                                    <span class="sub-title mr--5 text--upper">{{ item.taxType }} | </span> 
                                                                    <span class="sub-title">{{ computeTaxValue(item) }} </span> 
                                                                </div>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                </template>
                                                <template v-else>
                                                    <div>-</div>
                                                </template>
                                            </div>
                                        </div>

                                        <div v-if="!invoice.status !== 'draft'" class="invoice__details__group w-100">
                                            <div class="invoice__client--bio">
                                                <FormLabel title="Payment Timeline" class="mb--0" label-type="menu" />

                                                <template v-if="invoice.paymentTimeline && invoice.paymentTimeline.length">
                                                    <Timeline :items="invoice.paymentTimeline">
                                                        <template #item="item">
                                                            <p class="text--xs text--upper text-gray-600 text--semi--bold">
                                                                {{ invoice.currency }} {{ $format_number(item.amount) }}
                                                            </p>
                                                            <p class="text--xs text-gray-500">
                                                                {{ $format_date_time(item.dateAdded, true) }}
                                                            </p>
                                                        </template>
                                                    </Timeline>
                                                </template>
                                                <template v-else>
                                                    <div>-</div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="invoice__details__row__right">
                            <div class="invoice__details__divider pb--5">
                                <div class="flex align-items-center mb--20">
                                    <FormLabel title="Client Information" html-for="menu" label-type="title" class="mb--0" />
                                    <BaseButton 
                                        id="invoiceDetailsDropDown" 
                                        prefix-icon
                                        icon-name="link"
                                        variant="ghost" 
                                        class="rounded-hover ml--5"
                                        variant-style="secondary"
                                        @submit="viewDetails('client')"
                                    />
                                </div>
                                
                                <template v-if="client && client.name">
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Name
                                        </p>
                                        <p>
                                            {{ client?.name || '-' }}
                                        </p>
                                    </div>

                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Email
                                        </p>
                                        <p>
                                            {{ client.email || '-' }}
                                        </p>
                                    </div>
                                    
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Phone Number
                                        </p>
                                        <p>
                                            {{ client.phoneNumber || "-" }}
                                        </p>
                                    </div>
        
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Status
                                        </p>
                                        <p class="text--capitalize">
                                            <Status type="client" :value="client.status" />
                                        </p>
                                    </div>
    
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Country
                                        </p>
                                        <p>
                                            {{ client.country || '-' }}
                                        </p>
                                    </div>
    
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Address
                                        </p>
                                        <p v-if="client.address" class=" text--capitalize">
                                            {{ client.address }}, {{ client.state }}, {{ client.country }}
                                        </p>
                                        <p v-else>
                                            -
                                        </p>
                                    </div>
                                </template>
                               
                                <template v-else>
                                    <div class="flex align-items-center">
                                        <p class="text-gray-200 text--sm mr--5">
                                            Not available 
                                        </p>
                                        <span>
                                            <Status custom-text="Deleted" custom-status custom-status-class="tag--pink" :is-table="false" />
                                        </span>
                                    </div>
                                </template>
                            </div>

                            <div class="pt--15 invoice__details__divider">
                                <div class="flex align-items-center mb--20">
                                    <FormLabel title="Project Information" html-for="menu" label-type="title" class="mb--0" />
                                    <BaseButton 
                                        id="invoiceDetailsDropDown" 
                                        prefix-icon
                                        icon-name="link"
                                        icon- 
                                        class="rounded-hover ml--5"
                                        variant="ghost" 
                                        variant-style="secondary"
                                        @submit="viewDetails('project')"
                                    />
                                </div>

                                <template v-if="project && project.status">
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Project title
                                        </p>
                                        <p>
                                            {{ project.title || '-' }}
                                        </p>
                                    </div>
    
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Project status
                                        </p>
                                        <p>
                                            <Status type="project" :value="project.status" />
                                        </p>
                                    </div>
    
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Deadline
                                        </p>
                                        <p>
                                            {{ $format_date(project.deadline) || '-' }}
                                        </p>
                                    </div>
    
                                    <div class="invoice__client--bio flexed">
                                        <p>
                                            Estimated delivery time
                                        </p>
                                        <p>
                                            {{ $format_date(project.timeEstimate) || '-' }}
                                        </p>
                                    </div>
                                </template>
                                
                                <template v-else>
                                    <div class="flex align-items-center">
                                        <p class="text-gray-200 text--sm mr--5">
                                            Not available 
                                        </p>
                                        <span>
                                            <Status custom-text="Deleted" custom-status custom-status-class="tag--pink" :is-table="false" />
                                        </span>
                                    </div>
                                </template>
                            </div>

                            <div class="pt--15">
                                <div class="flex align-items-center justify-content-between mb--20">
                                    <div class="list--count">
                                        <FormLabel title="Reminders" label-type="title" class="mb--0 ml--5">
                                            <span class="ml--3 zm__count__badge text--xs">{{ invoice.meta.reminders.length }}</span>
                                        </FormLabel>
                                    </div>
                                    <BaseButton 
                                        prefix-icon
                                        class="p--0"
                                        icon-name="grid"
                                        variant="ghost" 
                                        icon-fill="#697386"
                                        variant-style="secondary"
                                        @submit="toggleRemindersModal"
                                    />
                                </div>
                                <div class="mt--15 grid gap-2">
                                    <template v-if="invoice.meta.reminders.length">
                                        <div v-for="reminder in invoice.meta.reminders" :key="reminder" class="flex align-items-center">
                                            <span class="mr--5 rounded-circle" style="width: 10px; height: 10px; border: 2px dotted grey;" />
                                            <span class="text--sm">
                                                {{ mapReminders[reminder] }}
                                            </span>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <ConfirmDeletion
            type="invoice"
            :show-modal="showDeleteModal"
            :loading="deleteLoading"
            @cancel="toggleDelete" 
            @delete="handleDeleteInvoice"
        />

        <MainModal 
            :show-modal="updateStatusModal" 
            size="sm" 
            title="Update status" 
            @close="updateStatusModal = false" 
        >
            <template #body>
                <div>
                    <form class="mb--10" @submit.prevent="">
                        <div class="filter-item-input">
                            <FormLabel title="Status" />
                            <select v-model="updateForm.status" class="form-select form-select-sm text--sm text--capitalize">
                                <option v-for="(status, index) in INVOICE_UPDATE_STATUSES" :key="index" :value="$str_to_lowercase(status)">
                                    {{ status }}
                                </option>
                            </select>
                        </div>

                        <template v-if="updateForm.status === 'partially paid'">
                            <Alert type="primary" classes="text--sm mt--20">
                                <template #text>
                                    <p class="text--xs">
                                        For partially paid invoices, enter the amount paid for payment tracking.
                                    </p>
                                </template>
                            </Alert>
    
                            <div>
                                <FormLabel title="Amount paid" html-for="hourlyRate" info-type="required" />
                                <money
                                    v-model="updateForm.amountPaid" 
                                    v-bind="config.number"
                                    :prefix="currencySymbol"
                                    class="form-control form-control-sm"
                                    spellcheck="false" 
                                    @keydown.native="preventKeys"
                                />
                                <p v-if="updateForm.amountPaidErr" class="form__err-msg">
                                    Amount value must be lesser than the invoice total amount
                                </p>
                            </div>
                        </template>
                    </form>
                </div>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        variant="outline"
                        label="Cancel" 
                        :disabled="updateLoading"
                        @submit="closeStatusUpdateModal"
                    />

                    <BaseButton 
                        :loading="updateLoading" 
                        :disabled="updateStatusDisabled" 
                        type="submit" 
                        label="Update" 
                        @submit="handleStatusUpdate"
                    />
                </div>
            </template>
        </MainModal>

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal || modalAction === 'showUpgradeModal'" 
            type="invoices" 
            @cancel="closeUpgradeModal"
        />

        <ProgressSuccess 
            :show-modal="showSuccessModal"
            btn-cta="Close screen"
            title="Invoice sent!"
            desc="Your invoice is being processed and your client will be notified in a little bit."
            @close="closeSuccessModal"
        />   

        <ManageRemindersModal 
            :initial-reminders="invoice.meta.reminders" 
            :show-modal="modalAction === 'showRemindersModal'"
            :loading="updateRemindersLoading"
            @cancel="modalAction = ''" 
            @complete="manageReminders"
        />

        <MainModal 
            :show-modal="modalAction === 'manageRemindersUpgrade'" 
            size="sm" 
            :close-btn="false" 
        >
            <template #body>
                <div class="modal--section">
                    <div class="upgrade--image">
                        <SvgAssets name="alarm" width="50px" height="50px" class="bg-white" />
                    </div>
                    <div class="upgrade--text">   
                        <div>
                            <div class="flex align-items-center mb--5">
                                <h2 class="upgrade--title mb--0">
                                    Set reminders
                                </h2>
                                <span
                                    v-if="accountPlan === 'free'" 
                                    class="status__map text--medium tag bold badge rounded-pill text--xxs tag--pink text--upper ml--10"
                                >
                                    <span 
                                        class="status__circle tag--pink__text text--xxs" 
                                    />
                                    Paid plans only
                                </span>
                            </div>
                            <p class="upgrade--subtitle error">
                                Reminders help control the frequency of email reminders your clients get for partially paid or no-show invoices.
                            </p>
                            <p class="upgrade--subtitle error">
                                To get started, go to your <strong>app settings</strong> and upgrade your account plan.
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        variant="outline"
                        label="Cancel" 
                        @submit="modalAction = ''"
                    />

                    <BaseButton 
                        type="submit" 
                        label="Go to settings" 
                        @submit="closeFeatUpgradeModal"
                    />
                </div>
            </template>
        </MainModal>

        <InvoiceProgress :show-modal="modalAction === 'showInvoiceProgressModal'" @close="modalAction = ''" />
    </div>
</template>

<script>
import { mapTaxTypes, mapReminders } from "@/utils/dummy";
import { INVOICE_UPDATE_STATUSES } from "@/utils/enums";
import {
    ConfirmDeletion,
    PageSpinner,
    Status,
    BaseButton,
    EmptyPage,
    MainModal,
    IconSvg,
    FormLabel,
    Alert,
    Timeline,
    UpgradeAccountModal,
    ProgressSuccess,
    ManageRemindersModal,
    List,
    ListItem,
    SvgAssets,
} from "@/components/ui/index";
import { supportedCurrenciesMap } from "@/plugins/countries";
import InvoiceProgress from "@/components/ui/modals/InvoiceProgress.vue";

export default {
    name: "InvoiceDetails",
    
    components: {
        ConfirmDeletion,
        PageSpinner,
        Status,
        BaseButton,
        EmptyPage,
        MainModal,
        IconSvg,
        FormLabel,
        Alert,
        Timeline,
        UpgradeAccountModal,
        ProgressSuccess,
        ManageRemindersModal,
        List,
        ListItem,
        SvgAssets,
        InvoiceProgress,
    },

    data() {
        return {
            mapTaxTypes,
            mapReminders,
            INVOICE_UPDATE_STATUSES,
            supportedCurrenciesMap,
            pageLoading: false,
            pageLoadErr: false,
            isPageDisabled: false,
            deleteLoading: false,
            updateLoading: false,
            showEditInvoiceView: false,
            invoice: this.generateInitialInvoiceData(),
            client: {},
            project: {},
            showDeleteModal: false,
            updateStatusModal: false,
            updateForm: {
                status: "",
                amountPaid: 0,
                amountPaidErr: "",
            },
            config: {
                number: {
                    decimal: ".",
                    thousands: ",",          // The currency name or symbol followed by a space.
                    prefix: "",
                    suffix: "",
                    precision: 0,
                    masked: false,
                },
            },
            unableToLoadPage: false,
            downloadLoading: false,
            showSuccessModal: false,
            updateRemindersLoading: false,
            modalAction: "",
        };
    },

    computed: {
        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        computedSubTotal() {
            if (this.invoice.meta?.items.length > 0) {
                const transformArr = this.invoice.meta?.items.map(item => item.itemPrice * item.itemQuantity);
                const sumItemsArr = transformArr.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
                return sumItemsArr;
            } else {
                return 0;
            }
        },

        computedDiscount() {
            if (this.invoice.meta?.discount?.type === "percentage" && this.invoice.meta?.discount?.value > 0) {
                return (this.invoice.meta?.discount?.value / 100) * this.computedSubTotal;
            }

            return this.invoice.meta?.discount?.value || 0;
        },

        discountBtnLabel() {
            if (!this.invoice.meta?.discount?.type) return "Discount";

            return `Discount${this.invoice?.meta?.discount?.type === "percentage" ? `(${this.invoice?.meta?.discount?.value}%):` : ""}`;
        },

        taxPercentage() {
            let percentageTotal = 0;

            if (this.invoice?.meta.tax.length) {                
                for (let i = 0; i < this.invoice?.meta.tax.length; i++) {
                    const item = this.invoice?.meta.tax[i];

                    if (item.taxType === "percentage") {
                        percentageTotal = item.taxValue + percentageTotal;
                    }
                }

                if (percentageTotal > 0) {
                    return {
                        show: true,
                        value: percentageTotal,
                    };
                }
            }

            return {
                show: false,
                value: undefined,
            };
        },

        computedTaxes() {
            let taxCount = 0;
            let percentageTotal = 0;

            if (this.invoice?.meta.tax.length) {                
                for (let i = 0; i < this.invoice?.meta.tax.length; i++) {
                    const item = this.invoice?.meta.tax[i];

                    if (item.taxType === "percentage") {
                        percentageTotal = item.taxValue + percentageTotal;
                    } else {
                        taxCount = item.taxValue + taxCount;
                    }
                }
            }

            if (percentageTotal > 0) {
                taxCount = taxCount + ((percentageTotal / 100) * this.computedSubTotal);
            }

            return taxCount;
        },

        updateStatusDisabled() {
            if (this.updateLoading || !this.updateForm.status) {
                return true;
            }

            if (this.updateForm.status === "partially paid") {
                // if (this.updateForm.amountPaid > this.invoice.totalAmount) {
                //     return true;
                // }

                if (!this.updateForm.amountPaid) {
                    return true;
                }
            }

            return false;
        },

        amountDue() {
            if (!this.invoice.paymentTimeline?.length) return this.invoice.totalAmount;

            const totalPaid = this.invoice.paymentTimeline.reduce((acc, curr) => acc + curr?.amount, 0);
            return this.invoice.totalAmount - totalPaid;
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        user() {
            return this.$store.getters["auth/profile"];
        },

        linkData() {
            return this.user?.paymentLinks?.find(x => x._id === this.invoice.paymentLinkId) || {
                _id: "",
                name: "",
                url: "",
            };
        },

        accountDetails() {            
            const currentAccDetails = this.user?.accounts?.find(x => x._id === this.invoice.accountDetailsId);
            
            return currentAccDetails || {
                accountName: "",
                bankName: "",
                accountNumber: "",
                routingNumber: "",
                swift: "",
            };
        },

        currencySymbol() {            
            return this.supportedCurrenciesMap[this.invoice.currency]?.symbol;
        },
    },

    watch: {
        "invoice.status": {
            handler(val) {
                this.updateForm.status = val;
            },
        },
    },

    async mounted() {
        await this.handleFetchInvoice();
    },

    methods: {
        closeUpgradeModal() {
            if (this.modalAction === "showUpgradeModal") {
                this.modalAction = "";
                return;
            }
            this.$store.dispatch("app/closeUpgradeModal", "invoices");
        },

        viewDetails(field) {
            switch (field) {
            case "client":                
                return this.$router.push(`/dashboard/clients/${this.client._id}`);

            case "project":
                return this.$router.push(`/dashboard/projects/${this.project._id}`);
            
            default:
                break;
            }
        },

        toggleRemindersModal() {
            this.modalAction = "";

            if (this.accountPlan === "free") {
                this.modalAction = "manageRemindersUpgrade";
                return;
            }

            this.modalAction = "showRemindersModal";
        },

        closeFeatUpgradeModal() {
            this.modalAction = "";
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        generateInitialInvoiceData() {
            return {
                invoiceNo: "",
                currency: "",
                amountPaid: null,
                createdAt: null,
                dueDate: null,
                _id: undefined,
                memo: "",
                meta: {
                    ccEmails: [],
                    tax: [],
                    reminders: [],
                    items: [{ itemName: "", itemPrice: 0.00, itemQuantity: 0, itemUnit: 0.00 }],
                    discount: {
                        type: "percentage",
                        value: "",
                    },
                    billingMethod: "automatic",                
                },
                status: "draft",
                title: "Invoice for",
                accountDetailsId: "",
                paymentLinkId: "",
                deadlineType: "30_days",
            };
        },

        openStatusModal() {
            this.updateForm.status = this.invoice.status;
            this.updateStatusModal = true;
        },

        splitCCEmails(emails) {
            if (!emails || typeof emails !== "string") return [];
            const res = emails.split(",") || [];
            return res;
        },

        computeTaxValue(tax) {
            if (tax.taxType === "percentage") {
                return `${tax.taxValue}%`;
            }
            return `${this.$country.getSymbol(this.invoice.currency, "currency")}${tax.taxValue}`;
        },

        backToInvoicesPage() {
            this.$router.push("/dashboard/invoices");
        },

        preventKeys(event) {
            if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105) || event.keyCode == 8 || event.keyCode == 9 || event.keyCode == 190 || event.keyCode == 37 || event.keyCode == 39) {
            } else {
                return event.preventDefault();
            }
        },

        toggleDelete() {
            this.showDeleteModal = !this.showDeleteModal;
        },

        createInvoicePayload(data) {
            const payload = {
                invoiceNo: this.invoice.invoiceNo,
                amountPaid: this.invoice.amountPaid,
                accountDetailsId: this.invoice.accountDetailsId,
                paymentLinkId: this.invoice.paymentLinkId,
                currency: this.invoice.currency.toLowerCase(),
                status: this.invoice.status,
                title: this.invoice.title,
                dueDate: this.invoice.dueDate,
                deadlineType: this.invoice.deadlineType || "30_days",
                datePaid: this.invoice.datePaid,
                memo: this.invoice.memo,
                meta: {
                    items: this.invoice.meta.items,
                    reminders: this.invoice.meta.reminders,
                    tax: this.invoice.meta.tax,
                    ccEmails: Array.isArray(this.invoice.meta.ccEmails) ? this.invoice.meta.ccEmails.join(",") : this.invoice.meta.ccEmails,
                    billingMethod: this.invoice.billingMethod || "automatic",
                    ...{ ...this.invoice.meta, ...data.meta },
                },
                status: this.invoice.status,
                totalAmount: this.invoice.totalAmount,
                ...data,
            };

            return payload;
        },

        async manageReminders(data) {
            try {  
                this.updateRemindersLoading = true;

                const payload = this.createInvoicePayload({
                    _id: this.invoice._id,
                    projectId: this.project._id,
                    clientId: this.client._id,
                    meta: {
                        ...this.invoice.meta,
                        reminders: data,
                    },
                });
                
                const res = await this.$store.dispatch("invoice/updateInvoice", payload);

                this.$toast.success("Reminders updated");
                this.invoice = {
                    ...this.invoice,
                    ...res,
                };
                this.modalAction = "";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.updateRemindersLoading = false;
            };
        },
        
        async handleDuplicateInvoice() {
            try {
                this.isPageDisabled = true;    
                const payload = {
                    ...this.createInvoicePayload({
                        clientId: this.client?._id,
                        projectId: this.project?._id,
                        meta: {
                            ...this.invoice.meta,
                            tax: this.invoice.meta.tax.map(x => ({ taxName: x.taxName, taxType: x.taxType, taxValue: x.taxValue })),
                            items: this.invoice.meta.items.map(x => ({ itemName: x.itemName, itemPrice: x.itemPrice, itemQuantity: x.itemQuantity, itemUnit: x.itemUnit })),
                        },
                    }),
                };

                delete payload.invoiceNo;
                const response = await this.$store.dispatch("invoice/duplicateInvoice", { payload, client: this.client });
                
                this.$toast.success("Duplicated invoice");
                this.$router.push({ name: "invoice-details-view", params: { id: response?._id } });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                if (errMsg.includes("ERR_US_03")) {
                    this.modalAction = "showUpgradeModal";
                    return;
                }
                this.$toast.error(errMsg);
            } finally {
                this.isPageDisabled = false;
            };
        },

        async handleDeleteInvoice() {
            try {
                const id = this.$route.params.id;
                
                if (!id) return;
    
                this.deleteLoading = true;
                
                await this.$store.dispatch("invoice/deleteInvoices", [id]);

                this.$toast.success("Invoice deleted");

                $("#deleteInvoice").modal("hide");

                this.$router.push({ path: "/dashboard/invoices" });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            };
        },

        async handleStatusUpdate() {
            try {  
                if (this.updateForm.status === this.invoice.status && this.updateForm.status !== "partially paid") {
                    this.$toast.error("Select a different invoice status");
                    return;
                }

                let amountPaid = 0;

                if (this.updateForm.status === "partially paid") {                    
                    amountPaid = typeof this.updateForm.amountPaid === "string" ? Number(this.updateForm.amountPaid.replaceAll(",", "")) : this.updateForm.amountPaid;

                    if (amountPaid > this.invoice.totalAmount) {
                        this.updateForm.amountPaidErr = "Enter in an amount less than the invoice total amount";
                        return;
                    }
                }
                
                this.updateForm.amountPaidErr = "";
                this.updateLoading = true;

                const payload = {
                    ...this.createInvoicePayload({
                        _id: this.invoice._id,
                        status: this.updateForm.status,
                        amountPaid,
                        clientId: this.client?._id,
                        projectId: this.project?._id,
                    }),
                };
                
                const res = await this.$store.dispatch("invoice/updateManualPayment", payload);

                this.invoice.status = this.updateForm.status;
                this.updateStatusModal = false;
                this.$toast.success("Invoice status updated");

                this.updateForm = {
                    status: "",
                    amountPaid: 0,
                };

                this.invoice = {
                    ...this.invoice,
                    ...res,
                };
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                if (errMsg.includes("clientId")) { // this means client has been deleted
                    this.$toast.error("Unable to update because the associated client has been deleted");
                } else if (errMsg.includes("projectId")) { // this means project has been deleted
                    this.$toast.error("Unable to update because the associated project has been deleted");
                } else {
                    this.$toast.error(errMsg);
                }
            } finally {
                this.updateLoading = false;
            };
        },

        async handleFetchInvoice() {
            try {
                this.pageLoadErr = false;
                this.unableToLoadPage = false;
                const id = this.$route.params.id;

                if (id === "undefined" || !id) {
                    this.unableToLoadPage = true;
                    return;
                }
                
                this.pageLoading = true;

                const res = await this.$store.dispatch("invoice/getInvoice", { id });

                if (!res) {                    
                    this.$toast.error("Invoice record does not exist!");
                    return this.$router.push("/dashboard/invoices");
                }

                this.invoice = res;
                this.client = res?.client || {};
                this.project = res?.project || {};
            } catch (err) {
                const errMsg = err?.response?.data?.message || err?.message;
                console.log({ errMsg });
                
                this.pageLoadErr = true;
                this.$toast.error(errMsg);
            } finally {
                this.pageLoading = false;
            }
        },

        goToEditPage() {
            this.$store.dispatch("invoice/setInvoice", { invoice: this.invoice, client: this.client, project: this.project });
            this.$router.push(`/dashboard/invoices/${this.$route.params.id}/edit`);
        },

        closeStatusUpdateModal() {
            this.updateForm = {
                status: "",
                totalAmount: 0,
            };
            this.updateStatusModal = false;
        },

        async downloadInvoice() {
            try {
                this.downloadLoading = true;

                const resp = await this.$store.dispatch("invoice/downloadInvoice", { 
                    id: this.invoice._id || this.$route.params.id,
                    config: {
                        responseType: "blob",
                        headers: {
                            "Accept": "application/pdf",
                        },
                    },
                });

                const blob = new Blob([resp.data], { type:  "application/pdf" });

                if (!blob || blob.size === 0) {
                    throw new Error("PDF file is empty");
                }

                const url = window.URL.createObjectURL(blob);
    
                // Create link element
                const link = document.createElement("a");
                link.href = url;
                
                // Get filename from response headers or set default
                const contentDisposition = resp.headers["content-disposition"];
                const fileName = contentDisposition
                    ? contentDisposition.split("filename=")[1].replace(/"/g, "")
                    : "rate_card.pdf";
                
                link.setAttribute("download", fileName);
                
                // Append link to body
                document.body.appendChild(link);
                
                // Start download
                link.click();
                
                // Clean up
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
             
                this.$toast.success("Invoice downloaded");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.downloadLoading = false;
            }
        },

        async handleSendInvoice() {
            try {
                if (!this.client._id || !this.project._id) {
                    this.$toast.error("Unable to send invoice with a deleted client or project");
                    return;
                }

                this.isPageDisabled = true;

                // trying to reduce the number of calls I make, so I passed already existing client/project info. 
                // (For data durability or consistency sake, this might not be the best idea)
                const payload = this.createInvoicePayload({
                    _id: this.invoice._id,
                    client: {
                        _id: this.client._id,
                        name: this.client.name,
                        country: this.client.country,
                        email: this.client.email,
                        address: this.client.address,
                        phoneNumber: this.client.phoneNumber,
                        logoUrl: this.client.logoUrl,
                    },
                    project: {
                        _id: this.project._id,
                        title: this.project.title,
                    },
                });

                await this.$store.dispatch("invoice/sendInvoice", { payload, type: "existing" });
                this.showSuccessModal = true;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.isPageDisabled = false;
            }
        },
        
        closeSuccessModal() {
            this.showSuccessModal = false;
            return this.$router.push({ path: "/dashboard/invoices" });
        },

        getPaymentMethodName(id) {
            const items = {
                "personal_account": "Manual payment via bank account",
                "payment_link": "",
                "automatic": "Automatic settlement",
            };

            return items[id] || "";
        },
    },
};
</script>

<style lang="scss" scoped>
    .row.hidden-xs {
        border-bottom: 1px solid #f5f5f5;
        margin-bottom: 2rem;
    }

    .form__row__left {
        max-width: 500px;
    }

    .invoice__single--btns {
        .row__item {
            padding-right: 0px !important;
        }
    }
</style>